import React, { useEffect, useMemo } from 'react';
import { Field, reduxForm, getFormValues } from 'redux-form';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import AccountKeyOutlineIcon from 'mdi-react/AccountKeyOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import EyeOffOutlineIcon from 'mdi-react/EyeOffOutlineIcon';
import { connect } from 'react-redux';
import renderSelectField from '../../../shared/components/form/Select';
import validate from './validateAccess';
import t, { partial } from '../../../util/translation/translation';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import FormCard from '../../../shared/components/custom/FormCard';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { hasAccess, isFilterable } from '../spotlightUtils';

const TileAccessForm = (props) => {
  const o = partial('OfferForm');
  const s = partial('shared');
  const e = partial('EventsForm');
  const p = partial('HighlightBannerForm');
  const {
    handleSubmit,
    news,
    initialize,
    groupsLevelsTiers,
    access,
    segments,
    accessFormValues = {},
    change,
    selectedEntityAccess,
  } = props;
  const { tiers, levels, groups } = groupsLevelsTiers;
  const targetAudience = useMemo(
    () => accessFormValues?.targetAudience,
    [accessFormValues]
  );
  // A chosen audience: any of levels/groups/tiers set (an empty list is
  // "all"), or at least one segment. The API always sends segments, an
  // empty list when there are none, so that alone isn't an audience.
  const getTargetAudience = () => {
    if (
      Array.isArray(access.levels) ||
      Array.isArray(access.groups) ||
      Array.isArray(access.tiers) ||
      access.segments?.length > 0
    ) {
      return 'targetAudience';
    }
    return 'all';
  };
  const getSelectedFields = (accessObject, objectField, title, searchArray) => {
    if (Array.isArray(accessObject[objectField])) {
      if (accessObject[objectField].length === 0) {
        return [{ label: e(`all${title}`), value: [] }];
      } else {
        // One no longer offered (e.g. a deleted tier) stays, by its id, so
        // saving again doesn't drop it unnoticed
        return accessObject[objectField].map(
          (accessItem) =>
            (searchArray || []).find((item) => item.value === accessItem) || {
              label: accessItem,
              value: accessItem,
            }
        );
      }
    } else {
      return [{ label: s('ignore'), value: null }];
    }
  };
  useEffect(() => {
    initialize({
      targetAudience: getTargetAudience(),
      level: getSelectedFields(access, 'levels', 'Levels', levels),
      tier: getSelectedFields(access, 'tiers', 'Tiers', tiers),
      group: getSelectedFields(access, 'groups', 'Groups', groups),
      segments: access?.segments?.length
        ? access.segments.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        : [{ label: s('ignore'), value: null }],
      noAccessView:
        typeof access.noAccessView === 'boolean'
          ? String(access.noAccessView)
          : 'true',
    });
    // Also when the options arrive, so saved choices get their names
  }, [news, access, groupsLevelsTiers]);
  const adjustDropdowns = (fieldName) => {
    if (accessFormValues[fieldName] && accessFormValues[fieldName].length > 1) {
      const [old, ...newOnes] = accessFormValues[fieldName];
      const newestOne =
        accessFormValues[fieldName][accessFormValues[fieldName].length - 1];
      if (old.value === null || Array.isArray(old.value)) {
        change(fieldName, newOnes);
      }
      if (newestOne.value === null || Array.isArray(newestOne.value)) {
        change(fieldName, [newestOne]);
      }
    }
  };
  useEffect(() => {
    adjustDropdowns('group');
    adjustDropdowns('level');
    adjustDropdowns('tier');
    adjustDropdowns('segments');
  }, [
    accessFormValues.level,
    accessFormValues.group,
    accessFormValues.tier,
    accessFormValues.segments,
  ]);
  useEffect(() => {
    if (
      selectedEntityAccess &&
      isFilterable(selectedEntityAccess?.selectedContent?.type)
    ) {
      change(
        'level',
        getSelectedFields(selectedEntityAccess, 'levels', 'Levels', levels)
      );
      change(
        'tier',
        getSelectedFields(selectedEntityAccess, 'tiers', 'Tiers', tiers)
      );
      change(
        'group',
        getSelectedFields(selectedEntityAccess, 'groups', 'Groups', groups)
      );
      change('noAccessView', String(selectedEntityAccess.noAccessView));
      // Chosen segments (not the "ignore" placeholder)
      if (accessFormValues.segments?.some((item) => item?.value)) {
        change('targetAudience', 'targetAudience');
      } else if (
        selectedEntityAccess.levels === null &&
        selectedEntityAccess.groups === null &&
        selectedEntityAccess.tiers === null
      ) {
        change('targetAudience', 'all');
      } else {
        change('targetAudience', 'targetAudience');
      }
    }
  }, [selectedEntityAccess]);
  const handleAudienceChange = (newAudience) => {
    const ignore = [{ label: s('ignore'), value: null }];
    if (newAudience === 'all') {
      change('level', ignore);
      change('tier', ignore);
      change('group', ignore);
      change('segments', ignore);
    } else {
      change('level', [{ label: e('allLevels'), value: [] }]);
      change('tier', [{ label: e('allTiers'), value: [] }]);
      change('group', [{ label: e('allGroups'), value: [] }]);
      change('segments', ignore);
    }
  };
  // Access comes from linked content (and is locked here) unless the tile
  // has no content, or its content has no access of its own
  const accessDisabled =
    selectedEntityAccess && selectedEntityAccess.isEditable !== true
      ? !(
          selectedEntityAccess?.selectedContent?.contentId === null ||
          !hasAccess(selectedEntityAccess?.selectedContent?.type)
        )
      : false;
  const isCustom = targetAudience && targetAudience !== 'all';
  // Header summary: how many of each list are chosen
  const chosen = (field) =>
    (accessFormValues?.[field] || []).filter(
      (item) => item && item.value !== null
    );
  const summary = isCustom
    ? [
        ['level', o('partnerlevelList')],
        ['group', o('partnergroupList')],
        ['tier', s('userTiers')],
        ['segments', t('HighlightBannerPage.segments')],
      ]
        .filter(([field]) => chosen(field).length)
        .map(([field, label]) => `${label}: ${chosen(field).length}`)
        .join(' · ') || p('accessCustom')
    : p('accessEveryone');

  return (
    <form onSubmit={handleSubmit} className="form">
      <FormCard
        title={s('access')}
        actions={<span className="tile-access__summary">{summary}</span>}
        className="tile-access mt-3 mb-3"
      >
        {accessDisabled && (
          <InfoBox icon={<LockOutlineIcon size={18} />} className="mb-3">
            {p('accessFromContent')}
          </InfoBox>
        )}
        <div
          className={`tile-access__body${
            accessDisabled ? ' tile-access__body--locked' : ''
          }`}
          aria-disabled={accessDisabled}
        >
          <div className="form__form-group">
            <span className="form-header">{p('accessWho')}</span>
            <Field
              name="targetAudience"
              component={ChoiceCards}
              ariaLabel={p('accessWho')}
              onPick={handleAudienceChange}
              options={[
                {
                  value: 'all',
                  icon: AccountGroupOutlineIcon,
                  label: p('accessEveryone'),
                  hint: p('accessEveryoneHint'),
                },
                {
                  value: 'targetAudience',
                  icon: AccountKeyOutlineIcon,
                  label: p('accessCustom'),
                  hint: p('accessCustomHint'),
                },
              ]}
            />
          </div>

          {isCustom && (
            <>
              <p className="tile-access__hint">{p('accessListsHint')}</p>
              <div className="form-grid">
                <div className="form__form-group">
                  <span className="form-header">{o('partnerlevelList')}</span>
                  <Field
                    name="level"
                    placeholder={o('selectLevels')}
                    options={levels}
                    component={renderSelectField}
                    multiple
                    disabled={accessDisabled}
                  />
                </div>
                <div className="form__form-group">
                  <span className="form-header">{o('partnergroupList')}</span>
                  <Field
                    name="group"
                    placeholder={o('selectGroup')}
                    options={groups}
                    component={renderSelectField}
                    multiple
                    disabled={accessDisabled}
                  />
                </div>
                <div className="form__form-group">
                  <span className="form-header">{s('userTiers')}</span>
                  <Field
                    name="tier"
                    placeholder={o('selectTier')}
                    options={tiers}
                    component={renderSelectField}
                    multiple
                    disabled={accessDisabled}
                  />
                </div>
                <div className="form__form-group">
                  <span className="form-header">
                    {t('HighlightBannerPage.segments')}
                  </span>
                  <Field
                    name="segments"
                    placeholder={p('selectSegments')}
                    options={segments}
                    component={renderSelectField}
                    multiple
                    disabled={accessDisabled}
                  />
                </div>
              </div>

              {/* Only matters with an audience: what everyone else sees */}
              <div className="form__form-group mb-0">
                <span className="form-header">{p('accessOthers')}</span>
                <Field
                  name="noAccessView"
                  component={ChoiceCards}
                  ariaLabel={p('accessOthers')}
                  options={[
                    {
                      value: 'true',
                      icon: LockOutlineIcon,
                      label: p('accessOthersLocked'),
                      hint: p('accessOthersLockedHint'),
                    },
                    {
                      value: 'false',
                      icon: EyeOffOutlineIcon,
                      label: p('accessOthersHidden'),
                      hint: p('accessOthersHiddenHint'),
                    },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      </FormCard>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'tile_access_form',
  validate,
});

const mapStateToProps = (state) => ({
  accessFormValues: getFormValues('tile_access_form')(state),
});

export default connect(mapStateToProps)(reduxF(TileAccessForm));
